import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { getTranslation } from 'common/react/utils/translations';
import { TeamLineupsFieldView } from './fieldview';
import { TeamLineupsListView } from './listview';
import { TeamLineupsContext } from 'widgets/mc-team-lineups/js/components/MCTeamLineups';
import { EmptyState } from 'common/react/components/EmptyState';

/**
 * Renders a wrapping component for the field and list views.
 * 
 * @param {object} match The match data object.
 * @param {bool} isAFLW Whether the match is an AFLW match.
 * @param {func} onTeamChange Function when a team selection is made.
 * @param {func} onTabViewChange Function when a tab view selection is made (Field/List).
 * @returns {JSX.Element} The wrapping component for the field and list views.
 */
export const FieldListView = ({ match, isAFLW = false, onTeamChange, onTabViewChange }) => {
    const [tabView, setTabView] = useState(PULSE.app.common.CONSTANTS.VIEWS.FIELD);

    const [selectedTeam, setSelectedTeam] = useState('-1');

    const handleTeamChange = (team) => {
        setSelectedTeam(team);
    };

    const handleTabViewChange = (tabView) => {
        setTabView(tabView);
    };

    const teamLineupsContext = useContext(TeamLineupsContext);

    useEffect(() => onTeamChange && onTeamChange(selectedTeam), [selectedTeam]);
    useEffect(() => onTabViewChange && onTabViewChange(tabView), [tabView]);

    const onTabClick = (view) => {
        /* eslint-disable camelcase */
        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.TAB,
            {
                eventData: {
                    tab_name: `${view} view`,
                    component_name: match.match.name,
                    component_content_type:
                        teamLineupsContext === 'match-centre'
                            ? PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES
                                  .MATCH_CENTRE.LINEUPS
                            : PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES
                                  .LINEUPS
                }
            }
        );
        /* eslint-enable camelcase */
        setTabView(view);
    };

    const hasTeamLineups = useMemo(() => {
        return (
            match?.matchRoster?.homeTeam?.positions.length > 0 &&
            match?.matchRoster?.awayTeam?.positions.length > 0
        );
    }, [match]);

    return (
        <>
            {hasTeamLineups ? (
                <div
                    className="team-lineups__sub-section team-lineups__sub-section--views"
                    tabIndex={0}
                >
                    <div className="team-lineups__tabs">
                        <button
                            className={`team-lineups__tab-toggle-button ${
                                tabView === PULSE.app.common.CONSTANTS.VIEWS.FIELD ? 'is-active' : ''
                            }`}
                            onClick={() => {
                                onTabClick(PULSE.app.common.CONSTANTS.VIEWS.FIELD);
                                handleTabViewChange(PULSE.app.common.CONSTANTS.VIEWS.FIELD);
                            }}
                        >
                            <SvgIcon icon="login" className="icon" />
                            {getTranslation(
                                'label.teamannouncements.fieldView'
                            )}
                        </button>
                        <button
                            className={`team-lineups__tab-toggle-button ${
                                tabView === PULSE.app.common.CONSTANTS.VIEWS.LIST ? 'is-active' : ''
                            }`}
                            onClick={() => {
                                onTabClick(PULSE.app.common.CONSTANTS.VIEWS.LIST);
                                handleTabViewChange(PULSE.app.common.CONSTANTS.VIEWS.LIST);
                            }}
                        >
                            <SvgIcon icon="list-bullet" className="icon" />
                            {getTranslation('label.teamannouncements.listView')}
                        </button>
                    </div>
                    <div className="team-lineups__tabs-content">
                        {tabView === PULSE.app.common.CONSTANTS.VIEWS.FIELD ? (
                            <TeamLineupsFieldView
                                {...match}
                                isAFLW={isAFLW}
                                onTeamChange={handleTeamChange}
                            />
                        ) : (
                            <TeamLineupsListView {...match} isAFLW={isAFLW} />
                        )}
                    </div>
                </div>
            ) : teamLineupsContext === 'match-centre' ? (
                <EmptyState
                    title={getTranslation('label.teamLineups.noDataTitle')}
                    summary={getTranslation('label.teamLineups.noDataText')}
                    cssClass="competition-nav__empty-state"
                />
            ) : (
                <div className="team-lineups__sub-section team-lineups__sub-section--empty-state">
                    {getTranslation('label.teamLineups.notAvailable')}
                </div>
            )}
        </>
    );
};

FieldListView.propTypes = {
    match: PropTypes.object.isRequired,
    isAFLW: PropTypes.bool,
    onTeamChange: PropTypes.func,
    onTabViewChange: PropTypes.func
};
